import clsx from 'clsx'
import Image from 'components/Common/Image'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import useHeldValue from 'hooks/useHeldValue'
import switchFunc from 'lib/function/switchFunc'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { extractReactNodeText } from 'lib/string/extractReactNodeText'
import { rem } from 'polished'
import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { InView } from 'react-intersection-observer'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import srOnly from 'styles/tools/sr-only'
import variables, { containerLarge } from 'styles/vendor/bootstrap/variables'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import type { BannerKind, PageBannerCarouselItem } from './types'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { bannerKindStyles } from './styles'
import PrefersReducedMotionContext from 'contexts/PrefersReducedMotionContext'
import {
  BANNER_BUTTON_COLOR_OVERRIDE_CLASS_NAME,
  BANNER_BUTTON_TEXT_COLOR_VAR,
  createBannerButtonColorStyles,
} from './bannerButtonColorStyles'
import { scrollToLogicalPosition } from 'LuxKit/Carousel/utils'

const NO_AUTOPLAY_SLIDE_DURATION = 0

// IconButton size="small" = 28px, gaps and margins = 12px
const CONTROL_SLOT_ONE_BUTTON = 28 + 12 + variables.gutterWidth / 2
const CONTROL_SLOT_TWO_BUTTONS = 28 + 12 + 28 + 12 + variables.gutterWidth / 2

const MOBILE_IMAGE_SIZE = 40

const buttonColorStyles = createBannerButtonColorStyles(`&.${BANNER_BUTTON_COLOR_OVERRIDE_CLASS_NAME}`)

const BannerSheet = styled.div`
  position: relative;
  overflow: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${bannerKindStyles}
`

const WidthContainer = styled.div`
  position: relative;
  margin-right: auto;
  margin-left: auto;
  width: 100%;

  max-width: ${containerLarge};
`

const CarouselWrapper = styled.div`
  position: relative;
`

const ScrollContainer = styled.div`
  padding-block: ${rem(12)};
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  mask-image: linear-gradient(
    to right,
    transparent 0,
    black var(--fade-width, 0px),
    black calc(100% - var(--fade-width, 0px)),
    transparent 100%
  );

  &.isCarousel {
    --fade-width: ${rem(16)};

    ${mediaQueryUp.desktop} {
      --fade-width: ${rem(32)};
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const Slide = styled(InView)`
  flex: 1 0 100%;
  scroll-snap-align: start;
  transition: padding .3s;

  ${mediaQueryDown.tablet} {
    &.has-mobile-controls {
      padding-bottom: ${rem(24)};

      &.button-currently-shown {
        padding-bottom: ${rem(40)};
      }
    }
  }
`

const SlideRow = styled.div`
  display: flex;
  gap: ${rem(12)};
  align-items: center;
  height: 100%;
  padding-right: ${rem(variables.gutterWidth / 2)};
  padding-left: ${rem(variables.gutterWidth / 2)};
  justify-content: flex-start;

  ${mediaQueryUp.desktop} {
    &.desktop-align-start {
      justify-content: flex-start;
    }
    &.desktop-align-center {
      justify-content: center;
    }
  }

  /* Reserve space for overlaid controls so they don't cover text */
  &.has-dismiss {
    padding-right: ${rem(CONTROL_SLOT_ONE_BUTTON)};
  }

  ${mediaQueryUp.desktop} {
    &.is-carousel {
      padding-left: ${rem(CONTROL_SLOT_ONE_BUTTON)};
      padding-right: ${rem(CONTROL_SLOT_ONE_BUTTON)};

      &.has-dismiss {
        padding-right: ${rem(CONTROL_SLOT_TWO_BUTTONS)};
      }

    }
  }
`

const ControlsRow = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  flex: 1;
  gap: ${rem(12)};
  padding-inline: ${rem(variables.gutterWidth / 2)};
  pointer-events: none;

  & > :not(.hidden) {
    pointer-events: auto;
  }

  ${mediaQueryDown.tablet} {
    &.has-mobile-controls {
      padding-bottom: ${rem(24)};

      &.button-currently-shown {
        padding-bottom: ${rem(40)};
      }
    }
  }
`

const MobileControls = styled(Group)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 ${rem(variables.gutterWidth / 2)} ${rem(12)};
  pointer-events: none;

  & > :not(.hidden) {
    pointer-events: auto;
  }

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const ControlsSpacer = styled.div`
  flex: 1;
`

const ControlButton = styled(IconButton)`
  pointer-events: auto;
  transition: opacity .3s;
  ${buttonColorStyles}

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

const ActionButton = styled(TextButton)`
  ${buttonColorStyles}
`

const HideableButton = styled(TextButton)`
  transition: opacity .3s;
  ${buttonColorStyles}

  &.hidden {
    opacity: 0;
  }
`

const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.3s ease;

  &.active {
    opacity: 1;
  }
`

const DesktopBackgroundImage = styled(Image)`
  position: absolute;
  inset: 0;
  display: none;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${mediaQueryUp.desktop} {
    display: block;

    &.active {
      opacity: 1;
    }
  }
`

const MobileImageWrapper = styled.div`
  flex-shrink: 0;
  width: ${rem(MOBILE_IMAGE_SIZE)};
  height: ${rem(MOBILE_IMAGE_SIZE)};
  border-radius: ${rem(4)};
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const LiveRegion = styled.div`
  ${srOnly}
`

const getPaletteType = switchFunc<App.Design.PaletteType, BannerKind>({
  'lux-plus': 'inverse',
  'loyalty-platinum': 'inverse',
  'loyalty-infinite': 'inverse',
  'none-inverse': 'inverse',
}, 'default')

interface Props extends Utils.PropsWithDataAttributes {
  items: Array<PageBannerCarouselItem>
  /**
   * Duration in milliseconds that each slide is displayed.
   * `0` = no autoplay
   * @default 0
   */
  slideDuration?: number
  className?: string
  id?: string
  /** Whether carousel arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean
}

function PageBannerCarousel({
  items,
  slideDuration: slideDurationProp = NO_AUTOPLAY_SLIDE_DURATION,
  className,
  id,
  arrowIconRtlFlip = false,
  ...rest
}: Props) {
  const intl = useIntl()
  const count = items.length
  const isCarousel = count > 1
  const scrollRef = useRef<HTMLDivElement>(null)
  const bannerRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(bannerRef, 'static')

  const prefersReducedMotion = useContext(PrefersReducedMotionContext)
  const isDesktop = useScreenSizeUp('desktop')
  // No autoplay on mobile/tablet, or when reduced motion is preferred
  const slideDuration = isDesktop && isCarousel && !prefersReducedMotion ? slideDurationProp : NO_AUTOPLAY_SLIDE_DURATION

  const scrollToIndex = useCallback((index: number) => {
    if (scrollRef.current) {
      const slideWidth = scrollRef.current.offsetWidth
      scrollToLogicalPosition(scrollRef.current, {
        left: slideWidth * index,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
    }
  }, [prefersReducedMotion])

  /*
    Note: In this component, the carousel scroll position is the source of truth for the current slide.
    `activeIndex` is merely synced to that scroll position so we can set the appropriate background,
    show/hide navigation buttons, etc.

    Therefore, don't use `setSyncedSlide` to navigate between slides - call `scrollToIndex` instead.
    That's why it's given the deliberately awkward name of `setSyncedSlideIndex` instead of `goToSlide`.
  */
  const { activeIndex, goToSlide: setSyncedSlideIndex, paused, setPaused } = useCarouselAutoPlay(
    count,
    slideDuration,
    { onAutoAdvance: scrollToIndex },
  )

  const activeItem = items[activeIndex] ?? items[0]

  const handleSlideInView = useCallback((inView: boolean, entry: IntersectionObserverEntry) => {
    if (inView) {
      const index = parseInt((entry.target as HTMLElement).dataset.index ?? '0', 10)
      setSyncedSlideIndex(index)
    }
  }, [setSyncedSlideIndex])

  const syncActiveSlide = useCallback(() => {
    const container = scrollRef.current
    if (!container) return
    const containerRect = container.getBoundingClientRect()
    let bestIndex = 0
    let bestOverlap = 0
    for (let i = 0; i < container.children.length; i++) {
      const child = container.children[i] as HTMLElement
      const childRect = child.getBoundingClientRect()
      const overlapLeft = Math.max(containerRect.left, childRect.left)
      const overlapRight = Math.min(containerRect.right, childRect.right)
      const overlap = Math.max(0, overlapRight - overlapLeft)
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestIndex = i
      }
    }
    setSyncedSlideIndex(bestIndex)
  }, [setSyncedSlideIndex])

  useEffect(() => {
    syncActiveSlide()
  }, [items, syncActiveSlide])

  const handlePrevious = useCallback(() => {
    scrollToIndex((activeIndex - 1 + count) % count)
  }, [activeIndex, count, scrollToIndex])

  const handleNext = useCallback(() => {
    scrollToIndex((activeIndex + 1) % count)
  }, [activeIndex, count, scrollToIndex])

  const handlePointerEnter = useCallback(() => {
    setPaused(true)
  }, [setPaused])

  const handlePointerLeave = useCallback(() => {
    setPaused(false)
  }, [setPaused])

  const anyItemHasMobileActionButton = items.some(item => !!item.actionButtonProps && !item.hideMobileActionButton)
  const anyItemHasDismiss = items.some(item => !!item.onDismiss)
  const activeItemHasMobileActionButton = !!activeItem.actionButtonProps && !activeItem.hideMobileActionButton
  const activeItemStyle = activeItem.style

  // When there is an item with a button, but the current item doesn't have a button, we want to continue
  // rendering an invisible button to reserve the space, so the height of the carousel doesn't change when
  // it moves between slides. We also want it to continue showing the same label/kind while we transition
  // its opacity to zero.
  const lastShownButtonProps = useHeldValue(
    activeItemHasMobileActionButton ? activeItem.actionButtonProps : undefined,
    !activeItemHasMobileActionButton,
    // Dummy button props, in case the first item doesn't have a button
    { default: { kind: 'primary', children: ' ' } },
  )

  const hasMobileControls = isCarousel || anyItemHasMobileActionButton
  const mobileControlsVisibilityClasses = clsx({
    'has-mobile-controls': hasMobileControls,
    'button-currently-shown': activeItemHasMobileActionButton,
  })

  const activeItemButtonColorClassName = clsx({
    [BANNER_BUTTON_COLOR_OVERRIDE_CLASS_NAME]: !!activeItemStyle?.[BANNER_BUTTON_TEXT_COLOR_VAR],
  })

  const inlineStyle = activeItemStyle

  const carouselLabel = intl.formatMessage({
    id: 'iJdbCztZF2fAEfXkZm4V',
    defaultMessage: 'Notifications',
    description: 'Accessible label for the page banner carousel region',
  })

  const liveRegionText = isCarousel ? intl.formatMessage({
    id: 'lLDX_rIrvA8F6ck3tSmC',
    defaultMessage: 'Slide {current} of {total}',
    description: 'Screen reader announcement for the current slide position in the banner carousel',
  }, { current: activeIndex + 1, total: count }) : undefined

  const nextSlideLabel = intl.formatMessage({
    id: '4SF56kh0yrRuFAaHER50',
    defaultMessage: 'Next slide',
    description: 'Accessible label for button advancing carousel to the next slide',
  })

  const prevSlideLabel = intl.formatMessage({
    id: 'Fwjh1-eupaAnbdYDknhx',
    defaultMessage: 'Previous slide',
    description: 'Accessible label for button advancing carousel to the previous slide',
  })

  const dismissBannerLabel = intl.formatMessage({
    id: 'KAuLX-aGUK4-LpD3ddnT',
    defaultMessage: 'Dismiss banner',
    description: 'Accessible label for button dismissing an info banner',
  })

  return <BannerSheet
    {...rest}
    ref={bannerRef}
    id={id}
    role="region"
    aria-roledescription={isCarousel ? 'carousel' : undefined}
    aria-label={carouselLabel}
    className={clsx(className, getThemeClassName(getPaletteType(activeItem.kind)), !activeItem.style && `kind-${activeItem.kind}`)}
    style={inlineStyle}
    onPointerEnter={isCarousel ? handlePointerEnter : undefined}
    onPointerLeave={isCarousel ? handlePointerLeave : undefined}
  >
    {items.map((item, index) => {
      if (!item.customBackground) return null
      return <BackgroundLayer
        key={item.id}
        className={clsx({ active: index === activeIndex })}
      >
        {item.customBackground}
      </BackgroundLayer>
    })}

    {items.map((item, index) => {
      if (!item.desktopImageId) return null
      return <DesktopBackgroundImage
        key={item.id}
        id={item.desktopImageId}
        fit="center"
        objectFit="cover"
        aspectRatio="8:1"
        dpr={1}
        className={clsx({ active: index === activeIndex })}
        alt=""
      />
    })}

    <WidthContainer>
      <CarouselWrapper>
        <ScrollContainer ref={scrollRef} className={clsx({ isCarousel })}>
          {items.map((item, index) => {
            let desktopAlign: 'start' | 'center'
            if (item.desktopAlign) {
              desktopAlign = item.desktopAlign
            } else {
              const textLength = extractReactNodeText(item.children).length
              desktopAlign = textLength > 80 ? 'start' : 'center'
            }
            const slideRowClassName = clsx(
              `desktop-align-${desktopAlign}`,
              {
                'is-carousel': isCarousel,
                'has-dismiss': anyItemHasDismiss,
              },
            )

            return <Slide
              key={item.id}
              onChange={handleSlideInView}
              threshold={0.5}
              skip={!isCarousel}
              data-index={index}
              className={mobileControlsVisibilityClasses}
            >
              <SlideRow className={slideRowClassName}>
                {item.leadingContent}
                <Group direction="horizontal" gap={12} horizontalAlign="start" desktopHorizontalAlign={desktopAlign} verticalAlign="center">
                  {item.mobileImageId && <MobileImageWrapper>
                    <Image
                      id={item.mobileImageId}
                      fit="center"
                      objectFit="cover"
                      width={MOBILE_IMAGE_SIZE}
                      height={MOBILE_IMAGE_SIZE}
                      alt=""
                    />
                  </MobileImageWrapper>}
                  <BodyTextBlock
                    startIcon={item.icon}
                    variant="medium"
                    align="start"
                    desktopAlign={desktopAlign}
                  >
                    <Group direction="vertical" gap={4}>
                      <div>
                        {item.children}
                        {item.textLinkProps && <>
                          {' '}<TextLink wrap="no-wrap" {...item.textLinkProps} />
                        </>}
                      </div>
                    </Group>
                  </BodyTextBlock>

                  {item.actionButtonProps && <CSSBreakpoint min="desktop">
                    <ActionButton
                      size="small"
                      {...item.actionButtonProps}
                      className={clsx(item.actionButtonProps.className, activeItemButtonColorClassName)}
                    />
                  </CSSBreakpoint>}
                </Group>
                {item.trailingContent}
              </SlideRow>
            </Slide>
          })}
        </ScrollContainer>

        <ControlsRow className={mobileControlsVisibilityClasses}>
          {isCarousel && <CSSBreakpoint min="desktop" dynamic>
            <ControlButton
              kind="tertiary"
              variant="default"
              size="small"
              onClick={handlePrevious}
              className={activeItemButtonColorClassName}
              aria-label={prevSlideLabel}
            >
              <LineAngleLeftIcon rtlFlip={arrowIconRtlFlip} />
            </ControlButton>
          </CSSBreakpoint>}

          <ControlsSpacer />

          {isCarousel && <CSSBreakpoint min="desktop" dynamic>
            <ControlButton
              kind="tertiary"
              variant="default"
              size="small"
              onClick={handleNext}
              className={activeItemButtonColorClassName}
              aria-label={nextSlideLabel}
            >
              <LineAngleRightIcon rtlFlip={arrowIconRtlFlip} />
            </ControlButton>
          </CSSBreakpoint>}

          <ControlButton
            kind="tertiary"
            size="small"
            aria-controls={id}
            onClick={activeItem.onDismiss}
            variant="default"
            className={clsx(activeItemButtonColorClassName, { hidden: !activeItem.onDismiss })}
            aria-hidden={!activeItem.onDismiss}
            tabIndex={activeItem.onDismiss ? undefined : -1}
            aria-label={dismissBannerLabel}
          >
            <LineTimesIcon />
          </ControlButton>
        </ControlsRow>

        <MobileControls direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="end">
          {isCarousel && <PaginationDots
            count={count}
            activeIndex={activeIndex}
            slideDuration={slideDuration}
            onSelect={scrollToIndex}
            paused={paused}
            onHoverChange={setPaused}
            position="start"
          />}
          {!isCarousel && <div />}

          {anyItemHasMobileActionButton && lastShownButtonProps && <HideableButton
            size="small"
            {...lastShownButtonProps}
            className={clsx(lastShownButtonProps.className, activeItemButtonColorClassName, { hidden: !activeItemHasMobileActionButton })}
            aria-hidden={!activeItemHasMobileActionButton}
            tabIndex={activeItemHasMobileActionButton ? undefined : -1}
          />}
        </MobileControls>
      </CarouselWrapper>
    </WidthContainer>
    {isCarousel && <LiveRegion aria-live="polite" aria-atomic="true">
      {liveRegionText}
    </LiveRegion>}
  </BannerSheet>
}

export default memo(PageBannerCarousel)
